/**
 * StructuredView — the primary research interface of the Connections
 * Explorer. Focus card + documented connections; selecting a connection
 * reveals its evidence (right rail on desktop, drawer on mobile).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, User, Building2, Crosshair, ChevronRight } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle } from "../ui/drawer";
import { EvidencePanel } from "./EvidencePanel";
import { KIND_META, groupOf, pathForEntity, portraitSrc } from "./explorerApi";
import { RecordThumb } from "../media/PublishedMedia";
import { ContextSummary } from "./ContextSummary";

const KIND_ICON = { person: User, organization: Building2, operation: Crosshair };

const FocusCard = ({ node, dossierCount }) => {
  const g = groupOf(node);
  const Icon = KIND_ICON[g] || Building2;
  return (
    <div className="border p-4 flex gap-4" style={{ borderColor: "#3A3630", background: "#12110e" }}
         data-testid={`focus-card-${node.slug}`}>
      <span className="w-16 h-20 shrink-0 flex items-center justify-center overflow-hidden"
            style={{ border: "1px solid #221F1C", background: "#0B0A0D" }}>
        {node.portrait_url ? (
          <img src={portraitSrc(node.portrait_url)} alt={node.name}
               className="max-w-full max-h-full object-contain" style={{ filter: "sepia(0.15)" }} />
        ) : (
          <RecordThumb targetId={node.slug}
                       className="max-w-full max-h-full object-contain"
                       fallback={<Icon size={18} className="text-[#454038]" />} />
        )}
      </span>
      <div className="min-w-0">
        <div className="font-mono text-[8px] uppercase tracking-[0.3em]"
             style={{ color: KIND_META[g]?.color }}>
          {KIND_META[g]?.label} · under focus
        </div>
        <h2 className="font-heading text-2xl text-[#E4DFD3] leading-tight mt-0.5">{node.name}</h2>
        {node.summary && !node.context_summary && (
          <p className="text-[11px] text-[#A39E93] leading-relaxed mt-1 line-clamp-2">{node.summary}</p>
        )}
        <ContextSummary node={node} />
        <div className="mt-1.5 flex gap-3 font-mono text-[9px] text-[#757065]">
          <span>{node.cameo_count} dossier{node.cameo_count === 1 ? "" : "s"}</span>
          {node.has_profile && (
            <Link to={pathForEntity(node)} className="text-[#A68F5E] hover:text-[#D92D20] transition-colors underline underline-offset-2"
                  data-testid={`open-profile-${node.slug}`}>
              full archive profile →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const ConnectionRow = ({ node, edge, selected, onSelect }) => {
  const g = groupOf(node);
  const Icon = KIND_ICON[g] || Building2;
  return (
    <button type="button" onClick={onSelect}
            className={`w-full text-left flex items-center gap-3 border p-2.5 transition-colors ${selected ? "border-[#D92D20]" : "hover:border-[#5A554C]"}`}
            style={{ borderColor: selected ? "#D92D20" : "#2D2A26", background: "#0d0c0a" }}
            data-testid={`connection-row-${node.slug}`}>
      <span className="w-9 h-11 shrink-0 flex items-center justify-center overflow-hidden"
            style={{ border: "1px solid #221F1C", background: "#0B0A0D" }}>
        {node.portrait_url ? (
          <img src={portraitSrc(node.portrait_url)} alt={node.name}
               className="max-w-full max-h-full object-contain" style={{ filter: "sepia(0.15)" }} loading="lazy" />
        ) : <Icon size={12} className="text-[#454038]" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] text-[#E4DFD3] leading-tight truncate">{node.name}</span>
        <span className="block font-mono text-[8px] uppercase tracking-[0.18em] mt-0.5"
              style={{ color: KIND_META[g]?.color }}>
          {KIND_META[g]?.label}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block font-mono text-[8px] uppercase tracking-[0.15em]"
              style={{ color: edge.strength === "corroborated" ? "#3FB950" : "#A68F5E" }}>
          {edge.strength}
        </span>
        <span className="block font-mono text-[8px] text-[#5A554C] mt-0.5">
          {edge.co_count} shared
        </span>
      </span>
      <ChevronRight size={12} className="text-[#454038] shrink-0" />
    </button>
  );
};

export const StructuredView = ({ graph, onFocus }) => {
  const [selected, setSelected] = useState(null); // {a, b}
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const focusNode = graph.nodes.find((n) => n.slug === graph.focus);
  const connections = useMemo(() => {
    if (!focusNode) return [];
    return graph.edges
      .filter((e) => e.a === focusNode.slug || e.b === focusNode.slug)
      .map((e) => {
        const other = graph.nodes.find(
          (n) => n.slug === (e.a === focusNode.slug ? e.b : e.a));
        return other ? { node: other, edge: e } : null;
      })
      .filter(Boolean)
      .sort((x, y) => y.edge.co_count - x.edge.co_count);
  }, [graph, focusNode]);

  const pick = (other) => {
    setSelected({ a: focusNode.slug, b: other });
    setDrawerOpen(true);
  };
  const follow = (slug) => {
    setSelected(null);
    setDrawerOpen(false);
    if (slug !== graph.focus) onFocus(slug);
  };

  if (!focusNode) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-6"
         data-testid="structured-view">
      <div>
        <FocusCard node={focusNode} />

        {graph.dossiers.length > 0 && (
          <>
            <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#757065] mt-6 mb-2">
              Documented appearances · {graph.dossiers.length}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {graph.dossiers.map((d) => (
                <Link key={d.slug} to={`/topic/${d.slug}`}
                      className="flex items-start gap-2 border p-2 hover:border-[#D92D20] transition-colors"
                      style={{ borderColor: "#2D2A26", background: "#0d0c0a" }}
                      data-testid={`appearance-${d.slug}`}>
                  <FileText size={11} className="text-[#A68F5E] mt-0.5 shrink-0" />
                  <span className="text-[11.5px] text-[#E4DFD3] leading-tight">{d.title}</span>
                </Link>
              ))}
            </div>
          </>
        )}

        <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#757065] mt-6 mb-2">
          Documented connections · {connections.length}
        </div>
        <div className="space-y-1.5" data-testid="connections-list">
          {connections.map(({ node, edge }) => (
            <ConnectionRow key={node.slug} node={node} edge={edge}
                           selected={selected?.b === node.slug || selected?.a === node.slug}
                           onSelect={() => pick(node.slug)} />
          ))}
          {connections.length === 0 && (
            <div className="font-mono text-[10px] text-[#757065] p-3 border border-dashed"
                 style={{ borderColor: "#2D2A26" }}>
              No co-appearing entities on record yet.
            </div>
          )}
        </div>
      </div>

      {/* Desktop evidence rail */}
      <div className="hidden lg:block">
        <div className="sticky top-24 border" style={{ borderColor: "#3A3630", background: "#12110e" }}>
          <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#757065] px-4 pt-4">
            Why this connection exists
          </div>
          {selected && isDesktop ? (
            <EvidencePanel a={selected.a} b={selected.b} onFollow={follow} />
          ) : (
            <div className="p-6 text-center font-mono text-[10px] text-[#5A554C]"
                 data-testid="evidence-placeholder">
              Select a connection to see the documented evidence behind it.
            </div>
          )}
        </div>
      </div>

      {/* Mobile evidence drawer — never mounts on desktop (vaul portals
          to document.body, so CSS hiding alone is not enough) */}
      {!isDesktop && (
        <Drawer open={drawerOpen && !!selected} onOpenChange={setDrawerOpen}>
          <DrawerContent className="bg-[#12110e] border-[#3A3630] max-h-[85vh] overflow-y-auto"
                         data-testid="evidence-drawer">
            <DrawerTitle className="sr-only">Connection evidence</DrawerTitle>
            {selected && <EvidencePanel a={selected.a} b={selected.b} onFollow={follow} />}
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
};
